// A person's avatar (DS-6): their picture when they uploaded one, else their initials on a
// palette.avatar colour. The colour comes from a stable hash of the name, so a student keeps the
// same colour on every screen. A flag shows only when the person chose a country; no default.
// Beside a printed name the avatar is hidden from screen readers; with a label (the profile form,
// where no name is beside it) it is an image named by that label.
import React from 'react';
import { palette, font, type } from '../design';
import { initials } from '../lib/format';

// Initials and the flag take this share of the avatar's width, never below the caption size.
const INITIALS_SCALE = 0.4;
const FLAG_SCALE = 0.4;

// WCAG minimum contrast for normal-size text. Initials are 12 to 26 px, so the normal rule holds.
const MIN_TEXT_CONTRAST = 4.5;
// Initials are white when white reads on the background, else this dark palette colour.
const LIGHT_TEXT = 'white';
const DARK_TEXT = 'espresso';

// The WCAG contrast ratio of two '#RRGGBB' colours, from 1 (same) to 21 (black on white).
export function contrastRatio(hexA, hexB) {
  const luminance = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const [r, g, b] = [16, 8, 0].map((shift) => {
      const c = ((n >> shift) & 255) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const [hi, lo] = [luminance(hexA), luminance(hexB)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// The palette name for initials on a palette background: white where it meets 4.5:1, else espresso.
export function initialsColour(background) {
  return contrastRatio(palette[background], palette[LIGHT_TEXT]) >= MIN_TEXT_CONTRAST ? LIGHT_TEXT : DARK_TEXT;
}

// The palette.avatar name for a person, picked by a string hash of the trimmed, lower-cased name.
export function avatarColour(name) {
  const key = String(name ?? '').trim().toLowerCase();
  let hash = 0;
  for (const ch of key) hash = (hash * 31 + ch.codePointAt(0)) >>> 0;
  return palette.avatar[hash % palette.avatar.length];
}

export function Avatar({ name, src, flag = null, size = 40, label = null }) {
  const box = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  const initialsPx = Math.max(type.caption, Math.round(size * INITIALS_SCALE));
  const flagPx = Math.max(type.caption, Math.round(size * FLAG_SCALE));
  const background = avatarColour(name);

  const face = src ? (
    <img src={src} alt="" style={{ ...box, display: 'block', objectFit: 'cover', objectPosition: 'top' }} />
  ) : (
    <span
      style={{
        ...box,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: palette[background],
        color: palette[initialsColour(background)],
        fontFamily: font.display,
        fontWeight: 700,
        fontSize: initialsPx,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {initials(name)}
    </span>
  );

  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': 'true' };
  return (
    <span {...a11y} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      {face}
      {flag && (
        <span style={{ position: 'absolute', right: -2, bottom: -2, fontSize: flagPx, lineHeight: 1 }}>{flag}</span>
      )}
    </span>
  );
}
